ScoreTrekBattle.League = ScoreTrekBattle.League || {};
ScoreTrekBattle.League.Matchday = ScoreTrekBattle.League.Matchday || {};

// Matchday details view (/league/matchday/<matchdayId>).
//
// Deliberately a sibling of ScoreTrekBattle.Tournament.Details, not an extension
// of it: the tournament view is built around a preset and the four participant
// lists (requests, participants, waitlist, blocklist) with their registration,
// payment and start flow. None of that exists for a matchday — both sides are
// squads fixed upfront, so the only "list" is the two rosters. What the two
// views share is the visual language, which is reused on the template/CSS level.
ScoreTrekBattle.League.Matchday.Details = (function() {
    var _this, STATUS, CONFIG, TIMER, SCROLL_TOP, MENU, OBSERVER, OBSERVER_VISIBLE_ELEMENTS;

    CONFIG = {
        // Roster order inside a line-up: captain first, then the starters, the
        // substitutes last. Mirrors the role order of the squad editor.
        ROLE_ORDER: ['captain', 'starter', 'substitute'],
        SIDES: ['home', 'guest'],
        // Disziplinen, deren Ausspielziel in Punkten statt in Gewinnspielen
        // angegeben wird.
        POINT_GAMES: ['game14', 'gameCarom'],
        POLL_DELAY: 10000,
        POLL_RUNNING_DELAY: 10000
    };

    TIMER = {};

    function resetStatus() {
        STATUS = {
            active: false,
            scrolling: true,
            view: null,
            $view: null,
            matchday: null,
            isManager: false,
            isAdmin: false,
            isCompleted: false,
            result: null,
            lineups: null,
            rounds: [],
            lineupChanges: null,
            players: {},
            teams: {},
            games: {},
            tables: null,
            matchesDialog: null,
            gotoGameId: null,
            pollTimestamp: 0,
            stateHash: '',
            isRefreshing: false,
            rendering: null
        };
    }
    resetStatus();

    function resetMenu() {
        MENU = {
            matchday: {
                id: '',
                label: __i18n('matchday/menu/matchday'),
                items: []
            },
            lineups: {
                id: '',
                label: __i18n('matchday/menu/lineups'),
                items: []
            },
            rounds: {
                id: '',
                label: __i18n('matchday/menu/rounds'),
                items: []
            }
        };
    }
    resetMenu();

    function resetObserver() {
        try {
            OBSERVER.disconnect();
        } catch(ignore) {}

        OBSERVER_VISIBLE_ELEMENTS = {};
        OBSERVER = null;
    }
    resetObserver();

    function loadScrollTop() {
        SCROLL_TOP = chmStorage.getSession('Matchday.ScrollTop');
        if (!SCROLL_TOP) {
            SCROLL_TOP = {};
        }
    }
    loadScrollTop();

    function storeScrollTop(scrollTop) {
        if (!STATUS.matchday || STATUS.scrolling) {
            return;
        }
        SCROLL_TOP[STATUS.matchday.id] = scrollTop;
        chmStorage.setSession('Matchday.ScrollTop', SCROLL_TOP);
    }

    function restoreScrollTop() {
        let status;

        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }
        status = STATUS;
        AnimationHandler.scrollTo(SCROLL_TOP[STATUS.matchday.id] || 0, true, STATUS.$view).then(() => {
            setTimeout(() => {
                status.scrolling = false;
            }, 1000);
        });
    }

    // ── View lifecycle ───────────────────────────────────────

    function onViewRender(matchdayId) {
        return new Promise(function(resolve) {
            STATUS.active = true;
            ScoreTrekBattle.View.loading('League.Matchday.Details.onViewRender', true);

            loadMatchday(matchdayId).catch(error => {
                chmDialog.error(error).then(() => {
                    ScoreTrekBattle.View.goBack();
                });
            }).finally(() => {
                ScoreTrekBattle.View.loading('League.Matchday.Details.onViewRender', false);
                renderMatchday().then(view => {
                    STATUS.active = true;
                    STATUS.view = view;
                    STATUS.$view = view.$view;
                    applyMatchdayData();

                    STATUS.rendering = Promise.all([
                        renderLineups(),
                        renderRounds()
                    ]).then(() => {
                        return renderMenu();
                    }).then(() => {
                        startPolling();
                    });

                    resolve(STATUS.$view);
                });
            });
        });
    }

    function onViewLoad() {
        return new Promise(function(resolve) {
            STATUS.active = true;
            applyMatchdayData();
            startPolling();

            (STATUS.rendering || Promise.resolve()).then(() => {
                setTimeout(() => {
                    startObserver();
                    restoreScrollTop();
                }, 0);
            });

            resolve();
        });
    }

    function onViewUpdate(matchdayId) {
        return new Promise(function(resolve) {
            // View.refresh() (link to the current path) calls this without args.
            if (!matchdayId && STATUS.matchday) {
                matchdayId = STATUS.matchday.id;
            }

            ScoreTrekBattle.View.goto(`/league/matchday/${matchdayId || ''}`, true);
            resolve();
        });
    }

    function onViewUnload() {
        return new Promise(function(resolve) {
            storeScrollTop(AnimationHandler.getScrollTop());
            stopPolling();
            resetObserver();
            resetMenu();
            resetStatus();
            resolve();
        });
    }

    // ── Polling ──────────────────────────────────────────────
    //
    // Dieselbe Mechanik wie in der Live-Ansicht des Turniers und derselbe
    // Endpunkt: /battle/poll/games kennt keinen Unterschied zwischen Turnier und
    // Spieltag, beide sind Events mit denselben Kindern. Adressiert wird hier
    // ueber eventId und eventType statt ueber tournamentId.

    function startPolling() {
        stopPolling();

        if (!STATUS.matchday || isFinalized()) {
            return;
        }

        TIMER.polling = setInterval(() => {
            if (!STATUS.active) {
                stopPolling();
                return;
            }
            pollGames();
        }, CONFIG.POLL_DELAY);

        TIMER.runningGames = setInterval(() => {
            if (!STATUS.active) {
                stopPolling();
                return;
            }
            pollRunningGames();
        }, CONFIG.POLL_RUNNING_DELAY);
    }

    function stopPolling() {
        clearInterval(TIMER.polling);
        TIMER.polling = null;
        clearInterval(TIMER.runningGames);
        TIMER.runningGames = null;
    }

    function pollGames() {
        Api.request('/battle/poll/games', {
            eventId: STATUS.matchday.id,
            eventType: 'matchday',
            timestamp: STATUS.pollTimestamp
        }).then(result => {
            if (!result || !STATUS.matchday) {
                return;
            }

            if (result.stateHash && (result.stateHash !== STATUS.stateHash)) {
                refreshMatchday(true).catch(ignore => {});
                return;
            }

            if (result.timestamp) {
                STATUS.pollTimestamp = result.timestamp;
            }

            if (result.teams) {
                updateTeams(result.teams);
            }

            if (result.games) {
                updateGames(result.games);
            }

            if (result.tables) {
                updateTables(result.tables);
            }
        }).catch(ignore => {});
    }

    // Eine laufende Partie aendert ihr Ergebnis, ohne dass `modified` am Spiel
    // haengt — die Dauer laeuft ohnehin weiter. Deshalb der zweite, gezielte
    // Abruf nur fuer das, was gerade am Tisch steht.
    function pollRunningGames() {
        let gameIds;

        gameIds = Object.values(STATUS.games).filter(game => {
            return (game.status === 'running');
        }).map(game => {
            return game.id;
        });

        if (gameIds.length === 0) {
            return;
        }

        Api.request('/battle/games/info', {
            eventId: STATUS.matchday.id,
            eventType: 'matchday',
            gameIds: gameIds
        }).then(result => {
            if (result && result.games) {
                updateRunningGames(result.games);
            }
        }).catch(ignore => {});
    }

    /**
     * Zieht Ergebnis und Dauer einer laufenden Partie nach.
     *
     * Bewusst getrennt von updateGames(): /battle/games/info liefert einen
     * reduzierten Datensatz ohne Status und Teams — den durch die volle
     * Darstellung zu schicken wuerde die Paarung aus dem Element loeschen.
     */
    function updateRunningGames(games) {
        Object.values(games).forEach(game => {
            let $game;

            $game = ScoreTrekBattle.$body.find(`[data-game-id="${game.id}"]`);

            if ($game.length === 0) {
                return;
            }

            if (STATUS.games[game.id]) {
                ['result1', 'result2', 'duration', 'durationSeconds', 'gameTarget', 'timeLimitSeconds'].forEach(gameKey => {
                    STATUS.games[game.id][gameKey] = game[gameKey];
                });
            }

            $game.attr({
                'data-game-target': game.gameTarget,
                'data-time-status': getTimeStatus(game)
            });

            ['result1', 'result2'].forEach(resultKey => {
                $game.find(`[data-id="${resultKey}"]`).text(game[resultKey] || 0);
            });

            $game.find('[data-id="duration"]').text(game.duration);
        });
    }

    function updateTeams(teams) {
        Object.values(teams).forEach(team => {
            STATUS.teams[team.id] = team;
            ScoreTrekBattle.Tournament.Match.updateTeam(team, STATUS.$view);
        });
    }

    function updateTables(tables) {
        STATUS.tables = tables;
        renderTablesData();
    }

    function updateGames(games) {
        Object.values(games).forEach(game => {
            STATUS.games[game.id] = game;

            ScoreTrekBattle.$body.find(`[data-game-id="${game.id}"]`).each(function() {
                applyGameData($(this), game, true);
            });
        });

        recalculateResult();
        renderStatusData();
        refreshMatchesDialog();

        // Mit dem Zustand der Partien aendert sich, ob die Aufstellung des
        // Durchgangs noch zu oeffnen ist. Der Knopf gehoert deshalb hierher und
        // nicht nur in den vollstaendigen Aufbau.
        refreshRoundActions();

        STATUS.isCompleted = areAllGamesDone();
        renderFinalization();
    }

    function areAllGamesDone() {
        let games;

        if (STATUS.matchday && (STATUS.matchday.status === 'done')) {
            return true;
        }

        games = Object.values(STATUS.games);

        return (games.length > 0) && games.every(game => {
            return (game.status === 'done');
        });
    }

    /**
     * Rechnet das Gesamtergebnis nach, wenn das Polling eine Partie
     * abgeschlossen hat.
     *
     * Die Wertung selbst kommt weiter vom Server (`pointsScheme`) — hier wird
     * nur gezaehlt, wer mehr Matches gewonnen hat. Ohne das bliebe der Stand
     * stehen, bis die Seite neu geladen wird.
     */
    function recalculateResult() {
        let count, scheme, home, guest, open, played, isDraw;

        if (!STATUS.result) {
            return;
        }

        scheme = STATUS.result.pointsScheme;
        count = 0;
        home = 0;
        guest = 0;
        open = 0;
        played = 0;

        // Ueber die Matches der Durchgaenge statt ueber die Partien: nur so ist
        // bekannt, wer zu einem Match angetreten ist — und damit, ob es ueber-
        // haupt zaehlt.
        STATUS.rounds.forEach(round => {
            (round.matches || []).forEach(match => {
                let game;

                game = getGameState(round, match);

                if (!game) {
                    return;
                }

                count++;

                if (game.status !== 'done') {
                    open++;
                    return;
                }

                // Ein Match, zu dem beide Seiten nicht angetreten sind, ist
                // erledigt, aber nicht gespielt. Es als gespielt zu fuehren,
                // wuerde ein 0:0 behaupten, das niemand erspielt hat.
                if (isEmptyMatch(round, match)) {
                    return;
                }

                played++;

                if (game.result1 > game.result2) {
                    home++;
                } else if (game.result2 > game.result1) {
                    guest++;
                }
            });
        });

        isDraw = (home === guest);

        STATUS.result = {
            home: {
                matches: home,
                points: isDraw ? scheme.draw : ((home > guest) ? scheme.win : scheme.loss)
            },
            guest: {
                matches: guest,
                points: isDraw ? scheme.draw : ((guest > home) ? scheme.win : scheme.loss)
            },
            isDraw: isDraw,
            isDecided: ((count > 0) && (open === 0)),
            hasResult: (played > 0),
            openMatches: open,
            pointsScheme: scheme
        };

        renderResult();
    }

    // ── Data ─────────────────────────────────────────────────

    function loadMatchday(matchdayId, keepCache) {
        return new Promise(function(resolve, reject) {
            Api.request('/battle/league/matchday/details', {
                id: matchdayId
            }).then(result => {
                if (!result || !result.matchday) {
                    if (!keepCache) {
                        clearLoadedMatchday();
                    }
                    reject(new Error('MATCHDAY_DOES_NOT_EXIST'));
                    return;
                }

                STATUS.matchday = result.matchday;
                STATUS.isManager = !!result.isManager;
                STATUS.isAdmin = !!result.isAdmin;
                STATUS.isCompleted = !!result.isCompleted;
                STATUS.result = result.result || null;
                STATUS.lineups = result.lineups || {};
                STATUS.rounds = result.rounds || [];
                STATUS.lineupChanges = result.lineupChanges || null;
                STATUS.tables = result.tables || null;
                STATUS.pollTimestamp = result.timestamp || 0;
                STATUS.stateHash = result.stateHash || '';
                mapPlayers();
                mapTeams(result.teams);
                mapGames();
                resolve(result);
            }).catch(error => {
                if (!keepCache) {
                    clearLoadedMatchday();
                }
                reject(error);
            });
        });
    }

    function clearLoadedMatchday() {
        STATUS.matchday = null;
        STATUS.isManager = false;
        STATUS.isAdmin = false;
        STATUS.isCompleted = false;
        STATUS.result = null;
        STATUS.lineups = null;
        STATUS.rounds = [];
        STATUS.lineupChanges = null;
        STATUS.players = {};
        STATUS.teams = {};
        STATUS.games = {};
        STATUS.tables = null;
        STATUS.stateHash = '';
    }

    // Die Paarungen der Durchgänge nennen nur IDs — die Namen stehen in den
    // Aufstellungen beider Seiten.
    function mapPlayers() {
        STATUS.players = {};

        CONFIG.SIDES.forEach(side => {
            let lineup;

            lineup = getLineup(side);

            if (!lineup || !lineup.roster) {
                return;
            }

            lineup.roster.forEach(player => {
                STATUS.players[player.id] = player;
            });
        });
    }

    function mapTeams(teams) {
        STATUS.teams = {};

        (teams || []).forEach(team => {
            STATUS.teams[team.id] = team;
        });
    }

    // Die Partien kommen je Durchgang und ueber die Match-Nummer adressiert. Fuer
    // das Polling wird zusaetzlich der direkte Zugriff ueber die Spiel-id
    // gebraucht.
    function mapGames() {
        STATUS.games = {};

        STATUS.rounds.forEach(round => {
            Object.values(round.games || {}).forEach(game => {
                STATUS.games[game.id] = game;
            });
        });
    }

    function refreshMatchday(isSilent) {
        return new Promise(function(resolve, reject) {
            let status;

            if (!STATUS.matchday || (isSilent && STATUS.isRefreshing)) {
                reject();
                return;
            }

            status = STATUS;
            status.isRefreshing = true;

            if (!isSilent) {
                ScoreTrekBattle.View.loading('League.Matchday.Details.refreshMatchday', true);
            }

            loadMatchday(STATUS.matchday.id, true).then(() => {
                applyMatchdayData();
                startPolling();

                STATUS.rendering = Promise.all([
                    renderLineups(),
                    renderRounds()
                ]).then(() => {
                    return renderMenu();
                }).then(() => {
                    startObserver();
                });

                resolve();
            }).catch(error => {
                if (!isSilent && error && error.message) {
                    chmDialog.error(error);
                }
                reject(error);
            }).finally(() => {
                status.isRefreshing = false;

                if (!isSilent) {
                    ScoreTrekBattle.View.loading('League.Matchday.Details.refreshMatchday', false);
                }
            });
        });
    }

    function getAnchor(key) {
        return STATUS.matchday ? `${STATUS.matchday.id}_${key}` : '';
    }

    function buildMenu() {
        resetMenu();

        if (!STATUS.matchday) {
            return;
        }

        MENU.matchday.id = `matchday_menu_${STATUS.matchday.id}_matchday`;
        MENU.lineups.id = `matchday_menu_${STATUS.matchday.id}_lineups`;
        MENU.rounds.id = `matchday_menu_${STATUS.matchday.id}_rounds`;

        MENU.matchday.items.push({
            icon: '<i class="fa-solid fa-location-dot"></i>',
            label: __i18n('matchday/menu/details'),
            anchor: getAnchor('details')
        });

        CONFIG.SIDES.forEach(side => {
            MENU.lineups.items.push({
                icon: `<i class="fa-solid ${(side === 'home') ? 'fa-house' : 'fa-van-shuttle'}"></i>`,
                label: __i18n(`matchday/side/${side}`),
                anchor: getAnchor(`lineup_${side}`)
            });
        });

        STATUS.rounds.forEach(round => {
            MENU.rounds.items.push({
                icon: '<i class="fa-kit fa-matches"></i>',
                label: round.name,
                anchor: getAnchor(`round_${round.index}`)
            });
        });
    }

    function renderMenu() {
        let promises;

        if (!STATUS.$view || !STATUS.matchday) {
            return Promise.resolve();
        }

        buildMenu();

        promises = Object.values(MENU).map(menuGroup => {
            return renderMenuGroup(menuGroup);
        });

        promises.push(renderMenuStatus());

        return Promise.all(promises).then($menuGroups => {
            STATUS.$view.find('[data-id="menu"]').empty().append($menuGroups);
            STATUS.$view.find('[data-id="menuToggle"]').off('click').on('click', function() {
                ScoreTrekBattle.$body.toggleClass('sideMenu-open');
            });
            renderStatusData();
        }).catch(error => {
            chmDialog.error(error);
        });
    }

    function renderMenuStatus() {
        return ScoreTrekBattle.Template.render('tournament/live/menu/status', {
            tournament: STATUS.matchday
        }).then(view => {
            return view.$view;
        });
    }

    function getMatchStats() {
        let stats;

        stats = {
            total: 0,
            pending: 0,
            ready: 0,
            running: 0,
            done: 0
        };

        STATUS.rounds.forEach(round => {
            (round.matches || []).forEach(match => {
                let game;

                game = getGameState(round, match);

                stats.total++;

                if (!game || !stats.hasOwnProperty(game.status)) {
                    stats.pending++;
                    return;
                }

                stats[game.status]++;
            });
        });

        return stats;
    }

    function renderStatusData() {
        let $status, stats, progress;

        if (!STATUS.$view || !STATUS.matchday) {
            return;
        }

        $status = STATUS.$view.find('[data-id="tournamentStatus"]');

        if ($status.length === 0) {
            return;
        }

        stats = getMatchStats();
        progress = Math.max(0, Math.min(100, (Math.round((stats.done / stats.total) * 100) || 0)));

        $status.toggleClass('isDone', (progress === 100));
        $status.get(0).style.setProperty('--progress', `${progress}%`);
        $status.find('[data-id="progress"]').text(`${progress}%`);
        $status.find('[data-id="gamesRunning"]').text(stats.running);
        $status.find('[data-id="gamesDone"]').text(stats.done);
        $status.find('[data-id="gamesTotal"]').text(stats.total);

        renderTablesData();
    }

    function renderTablesData() {
        let $status, tables, available, hasGamesReady;

        if (!STATUS.$view) {
            return;
        }

        $status = STATUS.$view.find('[data-id="tournamentStatus"]');
        tables = STATUS.tables || {};
        available = parseInt(tables.available, 10) || 0;

        $status.find('[data-id="tablesAvailable"]').text(available);
        $status.find('[data-id="tablesTotal"]').text(tables.total || 0);

        hasGamesReady = (STATUS.$view.find('[data-id="rounds"] [data-game-id][data-game-status="ready"]').length > 0);

        $status.find('[data-id="nextMatchIndicator"]').toggleClass(
            'isFlashing',
            (isManager() && !isFinalized() && (available > 0) && hasGamesReady)
        );
    }

    function findGameByStatus(status) {
        let found;

        found = null;

        STATUS.rounds.forEach(round => {
            (round.matches || []).forEach(match => {
                let game;

                if (found) {
                    return;
                }

                game = getGameState(round, match);

                if (game && (game.status === status)) {
                    found = game;
                }
            });
        });

        return found;
    }

    function gotoGame(gameId) {
        let $game;

        if (!STATUS.$view || !gameId) {
            return;
        }

        $game = STATUS.$view.find(`[data-game-id="${gameId}"]`);

        if ($game.length === 0) {
            return;
        }

        ScoreTrekBattle.Tournament.Live.forceToggleOpen($game);

        AnimationHandler.scrollIntoView($game).then(() => {
            AnimationHandler.pulse($game);
        });
    }

    function refreshMatchesDialog() {
        if (!STATUS.matchesDialog) {
            return;
        }

        STATUS.matchesDialog.$filters.find('[data-match-filter].isActive').trigger('click');
    }

    function renderAllMatches() {
        let promises;

        promises = [];

        STATUS.rounds.forEach(round => {
            (round.matches || []).forEach(match => {
                promises.push(renderRoundMatch(match, round));
            });
        });

        return Promise.all(promises);
    }

    function showMatchesDialog(filter) {
        if (!STATUS.matchday) {
            return;
        }

        if (STATUS.matchesDialog) {
            STATUS.matchesDialog.dialog.close();
            STATUS.matchesDialog = null;
            STATUS.gotoGameId = null;
        }

        renderAllMatches().then($matches => {
            STATUS.matchesDialog = ScoreTrekBattle.Tournament.Live.showMatchesDialog($matches, {
                filter: filter,
                hasControls: (isManager() && !isFinalized()),
                afterClose: () => {
                    let gameId;

                    gameId = STATUS.gotoGameId;

                    STATUS.gotoGameId = null;
                    STATUS.matchesDialog = null;

                    if (gameId) {
                        gotoGame(gameId);
                    }
                }
            });
        });
    }



    function renderMenuGroup(menuGroup) {
        if (menuGroup.items.length === 0) {
            return Promise.resolve('');
        }

        return ScoreTrekBattle.Template.render('tournament/live/menu/group', {
            menuGroup: menuGroup
        }, true).then(view => {
            let promises;

            promises = menuGroup.items.map(menuItem => {
                return renderMenuItem(menuItem);
            });

            return Promise.all(promises).then($menuItems => {
                let $items, $toggle, isOpen;

                $items = view.$view.find('[data-id="items"]');
                $toggle = view.$view.find('[data-id="toggle"]');

                $items.empty().append($menuItems);

                $toggle.on('click', function() {
                    let isOpenNow;

                    isOpenNow = (view.$view.attr('data-open') === 'no');

                    view.$view.attr('data-open', isOpenNow ? 'yes' : 'no');
                    $items.stop()[isOpenNow ? 'slideDown' : 'slideUp']();
                    ScoreTrekBattle.Tournament.Live.storeOpenState(menuGroup.id, isOpenNow);
                });

                isOpen = ScoreTrekBattle.Tournament.Live.getOpenState(menuGroup.id);

                if (!isOpen) {
                    view.$view.attr('data-open', 'no');
                    $items.hide();
                }

                return view.$view;
            });
        });
    }

    function renderMenuItem(menuItem) {
        return ScoreTrekBattle.Template.render('tournament/live/menu/item', {
            menuItem: {
                icon: menuItem.icon,
                label: __escapeHTML(menuItem.label),
                anchor: menuItem.anchor
            }
        }).then(view => {
            view.$view.on('click', function() {
                gotoAnchor(this.dataset.refAnchor);
            });

            return view.$view;
        });
    }

    function gotoAnchor(anchor) {
        let $element;

        if (!STATUS.$view || !anchor) {
            return;
        }

        $element = STATUS.$view.find(`[data-anchor="${anchor}"]`);

        if ($element.length === 0) {
            return;
        }

        ScoreTrekBattle.Tournament.Live.forceToggleOpen($element);

        AnimationHandler.scrollIntoView($element, $element.height() > (0.8 * window.innerHeight)).then(() => {
            AnimationHandler.pulse($element);
            activateElement(anchor, true);
        });
    }

    function startObserver() {
        resetObserver();

        if (!STATUS.$view) {
            return;
        }

        OBSERVER = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    OBSERVER_VISIBLE_ELEMENTS[entry.target.dataset.anchor] = entry.target;
                } else {
                    delete OBSERVER_VISIBLE_ELEMENTS[entry.target.dataset.anchor];
                }
            });
            activateElements(Object.values(OBSERVER_VISIBLE_ELEMENTS));
        }, {
            root: document,
            rootMargin: ScoreTrekBattle.View.getRootMargin(),
            threshold: 0
        });

        STATUS.$view.find('.observeIntersection[data-anchor]').each(function() {
            OBSERVER.observe(this);
        });
    }

    function activateElements(elements) {
        deactivateElements();

        elements.forEach(element => {
            activateElement(element.dataset.anchor);
        });
    }

    function activateElement(anchor, onlyOne) {
        if (!STATUS.$view) {
            return;
        }

        if (onlyOne) {
            deactivateElements();
        }

        STATUS.$view.find(`[data-id="menu"] [data-ref-anchor="${anchor}"]`).addClass('isActive');
    }

    function deactivateElements() {
        if (!STATUS.$view) {
            return;
        }

        STATUS.$view.find('[data-id="menu"] [data-ref-anchor]').removeClass('isActive');
    }

    // ── Rendering ────────────────────────────────────────────

    function renderMatchday() {
        return new Promise(function(resolve) {
            ScoreTrekBattle.Template.render(STATUS.matchday ? 'league/matchday/details' : 'league/matchday/empty', {
                matchday: STATUS.matchday,
                googleMapsUrl: getMapsUrl(STATUS.matchday)
            }, true).then(view => {
                if (STATUS.matchday) {
                    view.$view.find('[data-toggle-id]').each(function() {
                        let isOpen;

                        isOpen = ScoreTrekBattle.Tournament.Live.getOpenState(this.dataset.toggleId);

                        this.dataset.isOpen = isOpen ? 'yes' : 'no';
                        $(this).find('[data-ref="toggle"]').first()[isOpen ? 'show' : 'hide']();
                    });
                }

                resolve(view);
            });
        });
    }

    function isFinalized() {
        return !!(STATUS.matchday && STATUS.matchday.isFinalized);
    }

    function isManager() {
        if (!STATUS.isManager || !STATUS.matchday) {
            return false;
        }

        return !!(ScoreTrekBattle.Session.isAdminMode() || (ScoreTrekBattle.Session.isBattleManager() && ScoreTrekBattle.Session.isClubUser(STATUS.matchday.clubId)));
    }

    function hasGameHistory(game) {
        return !!(game && game.history && game.history.balls);
    }

    /**
     * Der Abschluss-Block. Regulaer abschliessen laesst sich ein Spieltag erst,
     * wenn keine Partie mehr offen steht; im Admin-Modus auch vorher, sonst
     * blieben steckengebliebene Spieltage fuer immer offen.
     */
    function renderFinalization() {
        let $view, hint;

        $view = STATUS.$view.find('[data-id="finalization"]');

        $view.find('[data-id="buttonFinalize"]').prop('disabled', isFinalized() || (!STATUS.isCompleted && !ScoreTrekBattle.Session.isAdminMode()));
        $view.find('[data-id="buttonUnseal"]').prop('disabled', !isFinalized());

        if (isFinalized()) {
            hint = __i18n('matchday/finalize/hint/sealed');
        } else if (!STATUS.isCompleted) {
            hint = __i18n('matchday/finalize/hint/running');
        } else {
            hint = __i18n('matchday/finalize/hint/ready');
        }

        $view.find('[data-id="finalizationHintText"]').html(hint);
    }

    function applyMatchdayData() {
        if (!STATUS.$view || !STATUS.matchday) {
            return;
        }

        ScoreTrekBattle.View.updateMenu('/league/matchdays');

        ScoreTrekBattle.$body.addClass('hasSideMenu');

        STATUS.$view.attr({
            'data-tournament-status': STATUS.matchday.statusInfo,
            'data-event-is-public': STATUS.matchday.isPublic ? 'yes' : 'no',
            'data-matchday-is-favorite': STATUS.matchday.isFavorite ? 'yes' : 'no'
        });

        // Without a league assignment the breadcrumb has nothing to show.
        if (!STATUS.matchday.groupLabel) {
            STATUS.$view.find('[data-ref-id="matchdayGroup"]').remove();
        }

        if (!STATUS.matchday.seasonLabel) {
            STATUS.$view.find('[data-ref-id="season"]').remove();
        }

        if (!STATUS.matchday.club || !STATUS.matchday.club.id) {
            STATUS.$view.find('[data-ref-id="host"]').remove();
        }

        STATUS.$view.find('[data-id="homeLabel"]').text(getLineupLabel('home'));
        STATUS.$view.find('[data-id="guestLabel"]').text(getLineupLabel('guest'));

        renderResult();
        renderFinalization();
    }

    /**
     * Das Gesamtergebnis der Begegnung und die Liga-Punkte daraus.
     *
     * Der Stand zaehlt nur gespielte Partien und ist deshalb auch waehrend des
     * Spieltags aussagekraeftig; solange etwas aussteht, sagt die Zeile
     * darunter, dass die Punkte noch nicht endgueltig sind.
     */
    function renderResult() {
        let result, $title;

        result = STATUS.result;
        $title = STATUS.$view.find('[data-id="matchdayTitle"]');

        if (!result || !result.hasResult) {
            $title.attr({'data-has-result': 'no', 'data-outcome': ''});
            return;
        }

        $title.attr({
            'data-has-result': 'yes',
            'data-outcome': result.isDraw ? 'draw' : ((result.home.matches > result.guest.matches) ? 'home' : 'guest')
        });

        $title.find('[data-id="homeMatches"]').text(result.home.matches);
        $title.find('[data-id="guestMatches"]').text(result.guest.matches);
        $title.find('[data-id="homePoints"]').text(result.home.points);
        $title.find('[data-id="guestPoints"]').text(result.guest.points);
        $title.find('[data-id="pointsState"]').text(result.isDecided
            ? __i18n('matchday/result/final')
            : __i18n('matchday/result/open', {count: result.openMatches}));
    }

    function getLineupLabel(side) {
        let lineup;

        lineup = getLineup(side);

        return (lineup && lineup.label) ? lineup.label : __i18n('matchday/lineup/unknownSquad');
    }

    function getLineup(side) {
        return (STATUS.lineups && STATUS.lineups[side]) ? STATUS.lineups[side] : null;
    }

    function getLineupBySide(side) {
        let found;

        found = null;

        CONFIG.SIDES.forEach(key => {
            let lineup;

            lineup = getLineup(key);

            if (lineup && (lineup.side === side)) {
                found = lineup;
            }
        });

        return found;
    }

    function canEditRoster(lineup) {
        return !!(isManager() && !isFinalized() && lineup);
    }

    function renderLineups() {
        let $lineups, promises;

        if (!STATUS.$view || !STATUS.matchday) {
            return Promise.resolve();
        }

        $lineups = STATUS.$view.find('[data-id="lineups"]');
        promises = [];

        CONFIG.SIDES.forEach(side => {
            promises.push(renderLineup(side, getLineup(side)));
        });

        return Promise.all(promises).then($views => {
            $lineups.empty().append($views);
        });
    }

    function renderLineup(side, lineup) {
        return new Promise(function(resolve) {
            // Normalized upfront so every placeholder resolves even for a side
            // whose squad was removed meanwhile.
            ScoreTrekBattle.Template.render('league/matchday/lineup', {
                side: side,
                anchor: getAnchor(`lineup_${side}`),
                lineup: {
                    squadId: (lineup && lineup.squadId) ? lineup.squadId : '',
                    label: getLineupLabel(side),
                    isKnown: !!(lineup && lineup.isKnown)
                }
            }, true).then(view => {
                let roster;

                roster = sortRoster((lineup && lineup.roster) ? lineup.roster : []);

                renderLineupClub(view.$view, lineup);
                view.$view.find('[data-id="rosterCount"]').text(roster.length > 0 ? roster.length : '');

                if (!lineup || !lineup.isReduced) {
                    view.$view.find('[data-id="reducedNotice"]').remove();
                }

                renderRosterActions(view.$view, lineup);

                renderRoster(view.$view.find('[data-id="rosterItems"]'), roster, lineup).then(() => {
                    resolve(view.$view);
                });
            });
        });
    }

    // ── Rounds ("Durchgänge") ────────────────────────────────
    //
    // Rendered with the tournament's own round and match markup. A matchday
    // round is a tournament round with a fixed set of matches, and the live
    // match element already covers everything those matches need: single
    // players, teams, "pending" and "bye". The one matchday specific is the
    // discipline per match, which rides in the target cell.

    function renderRounds() {
        let $rounds, promises;

        if (!STATUS.$view || !STATUS.matchday) {
            return Promise.resolve();
        }

        renderLineupChanges();

        $rounds = STATUS.$view.find('[data-id="rounds"]');

        // `live hasControls` schaltet die Steuerspalte der Match-Elemente frei —
        // dieselbe Klasse, an der auch die Live-Ansicht des Turniers haengt.
        // `data-event-type` blendet aus, was ein Spieltag nicht kennt.
        $rounds.attr('data-event-type', 'matchday').addClass('live').toggleClass('hasControls', isManager() && !isFinalized());

        if (STATUS.rounds.length === 0) {
            $rounds.empty();
            return Promise.resolve();
        }

        promises = STATUS.rounds.map(round => renderRound(round));

        return Promise.all(promises).then($views => {
            $rounds.empty().append($views);
        });
    }

    function renderRound(round) {
        // Escaped, unlike in the tournament: a round name there is generated,
        // here it comes straight out of the league preset.
        return ScoreTrekBattle.Template.render('tournament/live/round', {
            roundTitle: round.name
        }, true).then(view => {
            let toggleId, isOpen;

            toggleId = `matchday_${STATUS.matchday.id}_round_${round.index}`;
            isOpen = ScoreTrekBattle.Tournament.Live.getOpenState(toggleId);

            // The round menu (target, time limit, draw) has no counterpart on a
            // matchday, and neither has the random draw. Both hide themselves
            // once the classes that gate them are gone — the same switch
            // Tournament.Live flips through toggleElementHasControls().
            view.$view.removeClass('hasControls isRandomDraw');

            view.$view.attr({
                'data-round-index': round.index,
                'data-anchor': getAnchor(`round_${round.index}`),
                'data-toggle-id': toggleId,
                'data-is-open': isOpen ? 'yes' : 'no',
                'data-is-edited': round.isRevealed ? 'no' : 'yes'
            });

            renderRoundNotice(view.$view, round);
            renderRoundActions(view.$view, round);

            if (!isOpen) {
                view.$view.find('[data-ref="toggle"]').first().hide();
            }

            return renderRoundMatches(view.$view.find('[data-id="roundGroups"]'), round).then(() => {
                return view.$view;
            });
        });
    }

    // Before the reveal the state of both sides is the only thing that may be
    // shown. It goes into the round's notice band — the same element that
    // carries the tournament's manual draw hint. Its text is set here instead
    // of via data-i18n, which is only resolved while a template is rendered.
    function renderRoundNotice($round, round) {
        let $notice, states, hasCommitted;

        $notice = $round.find('.live-round-notice');

        if (round.isRevealed) {
            $notice.remove();
            return;
        }

        hasCommitted = CONFIG.SIDES.some(side => {
            return isSideCommitted(round, side);
        });

        states = CONFIG.SIDES.map(side => {
            let lineupState, isCommitted, colorClass;

            lineupState = getLineupState(round, side);
            isCommitted = (lineupState === 'committed');
            colorClass = {committed: 'color-green', pending: 'color-red', open: 'color-white-40'}[lineupState];

            return [
                `<span class="matchday-round-state-side">`,
                    `<span class="${colorClass} mr-tiny">`,
                        `<i class="fa-solid ${isCommitted ? 'fa-lock' : 'fa-lock-open'}"></i>`,
                    `</span>`,
                    `<span>${__escapeHTML(getLineupLabel(side))}</span>`,
                    `<span class="color-white-40 ml-tiny">`,
                        __escapeHTML(__i18n(isCommitted ? 'matchday/lineup/state/committed' : 'matchday/lineup/state/open')),
                    `</span>`,
                `</span>`
            ].join('');
        });

        $notice.attr('data-info-popup', 'matchday/lineup/hidden');
        $notice.find('.live-round-notice-icon').remove();
        $notice.find('.live-round-notice-info').html([
            `<div>${__escapeHTML(__i18n(hasCommitted ? 'matchday/lineup/hiddenShort' : 'matchday/lineup/openShort'))}</div>`,
            `<div class="matchday-round-state mt-tiny">${states.join('')}</div>`
        ].join(''));
    }

    /**
     * Die Knoepfe am Durchgang.
     *
     * Vor dem Enthuellen wird je Seite aufgestellt — Heim und Gast sind zwei
     * getrennte Entscheidungen und bekommen deshalb zwei getrennte Knoepfe.
     * Danach laesst sich der Durchgang noch einmal oeffnen, solange in ihm
     * nicht gespielt wurde. Ab dem ersten begonnenen Match ist er endgueltig,
     * und die Knoepfe fallen weg statt eine Aenderung anzubieten, die es nicht
     * mehr gibt.
     */
    function renderRoundActions($round, round) {
        let $actions;

        $actions = $round.find('[data-id="roundActions"]');
        $actions.empty();

        if (!isManager() || isFinalized()) {
            return;
        }

        if (round.isRevealed) {
            if (isRoundReopenable(round)) {
                $actions.append(renderRoundAction(round, 'reopen'));
            }

            return;
        }

        CONFIG.SIDES.forEach(side => {
            $actions.append(renderRoundAction(round, 'edit', side));
        });
    }

    function renderRoundAction(round, action, side) {
        let isCommitted, label;

        isCommitted = side ? isSideCommitted(round, side) : false;

        label = side
            ? __i18n('matchday/editor/button/setSide', {side: __i18n(`matchday/side/${side}`)})
            : __i18n('matchday/editor/button/reopen');

        return $('<span>')
            .addClass(side ? 'button button-secondary' : 'button button-tertiary')
            .attr('data-trigger-action', `matchday.lineup.${action}:${round.index}${side ? `:${side}` : ''}`)
            .append(
                $('<i>').addClass(side ? (isCommitted ? 'fa-solid fa-lock' : 'fa-solid fa-user-pen') : 'fa-solid fa-rotate-left'),
                $('<span>').text(label)
            );
    }

    // Zieht die Knoepfe der Durchgaenge nach, ohne die Matches neu aufzubauen.
    function refreshRoundActions() {
        if (!STATUS.$view) {
            return;
        }

        STATUS.rounds.forEach(round => {
            let $round;

            $round = STATUS.$view.find(`.live-round[data-round-index="${round.index}"]`);

            if ($round.length > 0) {
                renderRoundActions($round, round);
            }
        });
    }

    function renderRoundMatches($matches, round) {
        let promises;

        promises = (round.matches || []).map(match => renderRoundMatch(match, round));

        return Promise.all(promises).then($views => {
            $matches.empty().append($views);
        });
    }

    function renderRoundMatch(match, round) {
        let game;

        game = getGameState(round, match);

        // Die Partie liefert die id: an ihr haengen die data-ref-id-Anker, ueber
        // die das Polling Ergebnis und Dauer nachzieht.
        return ScoreTrekBattle.Template.render('tournament/live/match', {
            game: game || {id: ''}
        }, true).then(view => {
            view.$view.attr({
                'data-game-type': match.gameType,
                'data-match-index': match.index
            });

            view.$view.find('[data-id="matchName"]').text(ScoreTrekBattle.Tournament.Live.getGameName(match.index));
            view.$view.find('[data-id="gameTypeIcon"]').css('background-image', `url('/res/img/game/${match.gameType}.svg')`);
            view.$view.find('.live-match-target').attr('title', getMatchLabel(match));

            return applyGameData(view.$view, game, false).then(() => {
                return view.$view;
            });
        });
    }

    function getRoundGame(round, match) {
        return (round.games && round.games[match.index]) ? round.games[match.index] : null;
    }

    /**
     * Der aktuelle Stand einer Partie.
     *
     * `round.games` ist der Stand des letzten vollstaendigen Ladens; das
     * Polling und die Match-Aktionen schreiben nach STATUS.games. Wer wissen
     * will, wie es jetzt steht, muss dort nachsehen.
     */
    function getGameState(round, match) {
        let game;

        game = getRoundGame(round, match);

        if (!game || !game.id) {
            return game;
        }

        return STATUS.games[game.id] || game;
    }

    /**
     * Ob sich die Aufstellung eines Durchgangs noch einmal oeffnen laesst.
     *
     * Dieselbe Frage, die der Server beim Oeffnen stellt: sobald eine Partie
     * laeuft oder gespielt ist, bleibt die Aufstellung, wie sie war. Ein
     * Freilos zaehlt nicht mit — dort stand niemand am Tisch.
     *
     * Bewusst hier beantwortet und nicht vom Server mitgeliefert: die Antwort
     * aendert sich mit jeder Partie, und der Knopf soll sich mit ihr aendern —
     * nicht erst beim naechsten Laden der Seite.
     */
    function isRoundReopenable(round) {
        return !(round.matches || []).some(match => {
            let game;

            game = getGameState(round, match);

            if (!game || (['running', 'done'].indexOf(game.status) < 0)) {
                return false;
            }

            return !hasByeSide(round, match);
        });
    }

    // Ein Match, zu dem eine Seite niemanden gestellt hat.
    function hasByeSide(round, match) {
        return CONFIG.SIDES.some(side => {
            return isSideAbsent(round, side, match);
        });
    }

    // Ein Match, zu dem beide Seiten nicht angetreten sind.
    function isEmptyMatch(round, match) {
        return CONFIG.SIDES.every(side => {
            return isSideAbsent(round, side, match);
        });
    }

    // Solange die Aufstellung verdeckt ist, laesst sich darueber nichts sagen —
    // dann gilt eine Seite nicht als abwesend, sondern als unbekannt.
    function isSideAbsent(round, side, match) {
        let sideState;

        sideState = getRoundSide(round, side);

        if (!sideState || !sideState.assignment) {
            return false;
        }

        return ((sideState.assignment[match.index] || []).length === 0);
    }

    /**
     * Bringt ein Match-Element auf den Stand seiner Partie. Wird sowohl beim
     * Aufbau als auch aus dem Polling heraus aufgerufen, deshalb kommt alles,
     * was nicht am Element steht, ueber die Match-Nummer aus dem Preset.
     */
    function applyGameData($game, game, highlightUpdates) {
        let matchIndex, round, match, teamPromises, gameHtml, hasChanges, isRunning;

        matchIndex = parseInt($game.attr('data-match-index'), 10);
        round = getRoundByMatchIndex(matchIndex);
        match = getPresetMatch(round, matchIndex);

        if (!match) {
            return Promise.resolve();
        }

        gameHtml = $game.html();

        $game.attr({
            'data-game-id': game ? game.id : '',
            'data-game-status': game ? game.status : 'pending',
            'data-time-status': getTimeStatus(game),
            'data-game-target': game ? game.gameTarget : match.gameTarget,
            'data-match-has-history': hasGameHistory(game) ? 'yes' : 'no',
            'data-game-team1': game ? game.team1 : '',
            'data-game-team2': game ? game.team2 : ''
        });

        // Ein Ergebnis steht immer da. Eine Partie, die noch nicht gespielt ist,
        // steht 0:0 — ein leeres Feld waere keine andere Aussage, nur eine
        // unklarere.
        ['result1', 'result2'].forEach(resultKey => {
            $game.find(`[data-id="${resultKey}"]`).text(game ? (game[resultKey] || 0) : 0);
        });

        ScoreTrekBattle.Tournament.Live.applyGameStats($game, game);

        $game.find('[data-id="gameTarget"]').html((game ? game.gameTarget : match.gameTarget) || '<i class="fa-solid fa-infinity"></i>');
        $game.find('[data-id="duration"]').text(game ? game.duration : '');
        $game.find('[data-id="tableName"]').text((game && game.tableName) ? game.tableName : __i18n('table/none'));
        $game.find('[data-id="scoreboardName"]').text((game && game.scoreboardName) ? game.scoreboardName : __i18n('table/scoreboard/none'));

        // Tisch und Dauer sagen nur ueber eine laufende Partie etwas aus.
        isRunning = !!(game && (game.status === 'running'));
        $game.find('[data-id="matchDuration"],[data-id="matchTable"]')[isRunning ? 'show' : 'hide']();

        teamPromises = CONFIG.SIDES.map((side, index) => {
            let teamKey;

            teamKey = `team${index + 1}`;

            return renderMatchTeam(round, side, match, game ? game[teamKey] : '').then($team => {
                $game.find(`[data-id="${teamKey}"]`).empty().append($team);
            });
        });

        return Promise.all(teamPromises).then(() => {
            hasChanges = (gameHtml !== $game.html());

            if (highlightUpdates && hasChanges) {
                AnimationHandler.highlight($game);
            }
        });
    }

    function getTimeStatus(game) {
        if (!game || !game.timeLimitSeconds) {
            return 'ok';
        }

        return (game.durationSeconds > game.timeLimitSeconds) ? 'overdue' : 'ok';
    }

    function getRoundByMatchIndex(matchIndex) {
        return STATUS.rounds.find(round => {
            return (round.matches || []).some(match => {
                return (match.index === matchIndex);
            });
        }) || null;
    }

    function getPresetMatch(round, matchIndex) {
        if (!round) {
            return null;
        }

        return (round.matches || []).find(match => {
            return (match.index === matchIndex);
        }) || null;
    }

    // The target cell only has room for the number, so discipline and wording
    // go into its title.
    function getMatchLabel(match) {
        let parts;

        parts = [__i18n(`gameType/${match.gameType}`)];

        if (match.gameTarget) {
            parts.push(__i18n(
                (CONFIG.POINT_GAMES.indexOf(match.gameType) >= 0) ? 'matchday/match/points' : 'matchday/match/wins',
                {target: match.gameTarget}
            ));
        }

        if (match.alternatingBreak) {
            parts.push(__i18n('modeInfo/alternatingBreak/yes'));
        }

        return parts.join(' · ');
    }

    // All three cases are already covered by the tournament's match team
    // element: a covered line-up and an unfilled match render as "pending",
    // exactly like a tournament match whose team is not drawn yet — only with
    // their own label and icon.
    //
    // Sobald die Partie steht, ist ihr Team die Quelle: nur so ziehen Aufgabe,
    // Disqualifikation und Namensaenderungen ueber das Polling nach.
    function renderMatchTeam(round, side, match, teamId) {
        let sideState, playerIds, teamSize, participants, lineupState;

        teamSize = match.teamSize || ((round && round.teamSize) ? round.teamSize : 1);
        sideState = getRoundSide(round, side);

        if (!sideState || !sideState.isVisible || !sideState.assignment) {
            lineupState = getLineupState(round, side);

            return ScoreTrekBattle.Tournament.Match.renderPending(
                __i18n((lineupState === 'committed') ? 'matchday/lineup/committedShort' : 'matchday/lineup/openShort'),
                {
                    teamSize: teamSize,
                    icon: `<i class="fa-solid ${(lineupState === 'committed') ? 'fa-lock' : 'fa-lock-open'}"></i>`
                }
            ).then($team => {
                return $team.attr('data-lineup-state', lineupState);
            });
        }

        playerIds = sideState.assignment[match.index] || [];

        if (playerIds.length === 0) {
            // A side may take the field short-handed. The unfilled match is a
            // bye — same wording and icon as in a tournament.
            return ScoreTrekBattle.Tournament.Match.renderPending(__i18n('player/bye'), {
                teamSize: teamSize,
                icon: '<i class="fa-solid fa-user-slash"></i>'
            });
        }

        if (teamId && STATUS.teams[teamId]) {
            return renderTeam(STATUS.teams[teamId], teamSize);
        }

        participants = playerIds.map(playerId => {
            return STATUS.players[playerId];
        }).filter(player => {
            return !!player;
        });

        return renderTeam({
            id: '',
            name: getLineupLabel(side),
            participants: participants,
            handicap: 0
        }, teamSize);
    }

    function renderTeam(team, teamSize) {
        return ScoreTrekBattle.Tournament.Match.renderTeam(STATUS.$view, team, {
            teamSize: teamSize,
            toggle: 'disabled',
            allowHighlight: 'no',
            isClickable: false
        }).then($team => {
            $team.find('[data-trigger-action="participant.info"]').each(function() {
                $(this).attr({
                    'data-trigger-action': 'matchday.player.info',
                    'data-matchday-player-id': $(this).attr('data-participant-id') || ''
                });
            });

            return $team;
        });
    }

    function isSideCommitted(round, side) {
        let sideState;

        sideState = getRoundSide(round, side);

        return !!(sideState && sideState.isCommitted);
    }

    function getLineupState(round, side) {
        if (isSideCommitted(round, side)) {
            return 'committed';
        }

        return CONFIG.SIDES.some(key => {
            return isSideCommitted(round, key);
        }) ? 'pending' : 'open';
    }

    // Die Zustände kommen serverseitig als own/opponent, die Anzeige denkt in
    // Heim und Gast.
    function getRoundSide(round, side) {
        let lineup;

        lineup = getLineup(side);

        if (!round.sides || !lineup || !lineup.side) {
            return null;
        }

        return round.sides[lineup.side] || null;
    }

    function renderLineupChanges() {
        let $notice, changes;

        $notice = STATUS.$view.find('[data-id="lineupChanged"]');
        changes = STATUS.lineupChanges;

        if (!changes || !changes.count) {
            $notice.css('display', 'none');
            return;
        }

        $notice.css('display', '');
        $notice.find('[data-id="lineupChangedInfo"]').text(changes.last
            ? __i18n('matchday/lineup/changedInfoLast', {count: changes.count, date: changes.last.unlockedAt})
            : __i18n('matchday/lineup/changedInfo', {count: changes.count}));
    }

    function renderLineupClub($lineup, lineup) {
        let club;

        club = (lineup && lineup.club) ? lineup.club : null;

        if (!club) {
            $lineup.find('[data-id="clubRow"]').remove();
            return;
        }

        $lineup.find('[data-id="clubLogo"]').css('background-image', `url('${club.logo}')`);
        $lineup.find('[data-id="clubName"]').text(club.name || '');
    }

    // Mannschaftsführer zuerst, dann Stamm-, dann Ersatzspieler, zuletzt wer
    // gar nicht im Kader stand; innerhalb der Rolle alphabetisch.
    function sortRoster(roster) {
        return roster.slice().sort(function(playerA, playerB) {
            let indexA, indexB;

            indexA = getRoleOrder(playerA.role);
            indexB = getRoleOrder(playerB.role);

            if (indexA !== indexB) {
                return indexA - indexB;
            }

            return String(playerA.fullName).localeCompare(String(playerB.fullName));
        });
    }

    function getRoleOrder(role) {
        let index;

        index = CONFIG.ROLE_ORDER.indexOf(role);

        return (index < 0) ? CONFIG.ROLE_ORDER.length : index;
    }

    function renderRoster($roster, roster, lineup) {
        let promises, canEdit;

        if (roster.length === 0) {
            return renderEmptyRoster($roster, lineup);
        }

        promises = [];
        canEdit = canEditRoster(lineup);

        roster.forEach(player => {
            promises.push(ScoreTrekBattle.League.Matchday.Roster.renderPlayer(player, {
                canRemove: (canEdit && !!player.isMatchdayOnly)
            }).then($item => {
                return $item.attr('data-trigger-action', 'matchday.player.info');
            }));
        });

        return Promise.all(promises).then($items => {
            $roster.empty().append($items);
        });
    }

    // Two distinct empty states: a free-text opponent has no squad in the system
    // at all, a configured squad may simply have nobody assigned yet.
    function renderEmptyRoster($roster, lineup) {
        return ScoreTrekBattle.Template.render('league/matchday/lineupItemEmpty', {
            label: (lineup && lineup.isKnown) ? 'matchday/lineup/empty' : 'matchday/lineup/noSquad'
        }).then(view => {
            $roster.empty().append(view.$view);
        });
    }

    function showPlayerInfo(matchdayPlayerId) {
        let player;

        player = STATUS.players[matchdayPlayerId || ''] || null;

        if ((player === null) || !player.player) {
            return;
        }

        ScoreTrekBattle.Information.showParticipantInfo({
            id: null,
            playerId: player.playerId || '',
            fullName: player.fullName || '',
            player: player.player
        });
    }

    /**
     * Die angetretene Mannschaft laesst sich schon hier ergaenzen, nicht erst
     * beim Aufstellen.
     *
     * Damit hat der Turnierleiter die Wahl: erst die Angetretenen pflegen und
     * dann aufstellen, oder waehrend des Aufstellens ergaenzen. Beide Wege
     * fuehren ueber dieselbe Spielersuche.
     */
    function renderRosterActions($lineup, lineup) {
        let $actions;

        $actions = $lineup.find('[data-id="rosterActions"]');

        if (!canEditRoster(lineup)) {
            $actions.remove();
            return;
        }

        ScoreTrekBattle.League.Matchday.Roster.renderOption({
            type: 'add',
            icon: '<i class="fa-solid fa-plus"></i>',
            label: __i18n('matchday/lineup/addPlayer')
        }).then($item => {
            $item.on('click', function() {
                addRosterPlayer(lineup);
            });

            $actions.empty().append($item);
        });
    }

    // Heim und Gast sind eine Darstellung — die Aufstellung wird ueber die
    // stabile Zuordnung own/opponent adressiert, die die Liste selbst mitbringt.
    function addRosterPlayer(lineup) {
        ScoreTrekBattle.League.Matchday.Roster.addPlayer(STATUS.matchday.id, lineup.side, {
            getPlayers: function() {
                let current;

                current = getLineupBySide(lineup.side);

                return (current && current.roster) ? current.roster : [];
            },
            onAdded: function() {
                refreshMatchday().catch(ignore => {});
            }
        });
    }

    function removeRosterPlayer(matchdayPlayerId, playerName) {
        chmDialog.confirm(__i18n('dialog/confirm/remove/text', {colorClass: 'color-blue', reference: playerName}, true), {
            title: __i18n('dialog/confirm/remove/title'),
            buttonYes: __i18n('button/remove'),
            buttonNo: __i18n('button/cancel')
        }).then(() => {
            ScoreTrekBattle.View.loading('League.Matchday.Details.removeRosterPlayer', true);

            Api.request('/battle/league/matchday/lineup/player/remove', {
                id: STATUS.matchday.id,
                matchdayPlayerId: matchdayPlayerId
            }).then(() => {
                refreshMatchday().catch(ignore => {});
            }).catch(error => {
                chmDialog.error(error);
            }).finally(() => {
                ScoreTrekBattle.View.loading('League.Matchday.Details.removeRosterPlayer', false);
            });
        }).catch(ignore => {});
    }

    function getMapsUrl(matchday) {
        if (!matchday) {
            return 'javascript:';
        }

        return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${matchday.locationStreet}, ${matchday.locationZip} ${matchday.locationCity}`)}`;
    }

    // ── Match-Aktionen ───────────────────────────────────────
    //
    // Ab dem Enthuellen ist ein Liga-Match ein gewoehnliches Battle-Match: es
    // laeuft ueber dieselben Endpunkte wie im Turnier. Die Aktionsnamen sind
    // dieselben, weil es dasselbe HTML ist — jede Ansicht behandelt nur die
    // Partien, die sie selbst kennt.

    function getOwnGame(element) {
        let gameId;

        gameId = $(element).closest('[data-game-id]').attr('data-game-id');

        return (gameId && STATUS.games[gameId]) ? STATUS.games[gameId] : null;
    }

    function triggerGameAction(action, params) {
        return new Promise(function(resolve, reject) {
            ScoreTrekBattle.View.loading('League.Matchday.Details.triggerGameAction', true);

            Api.request(`/battle/game/${action}`, params).then(result => {
                if (!result || !result.hasOwnProperty('affectedGames')) {
                    reject(new Error('PERSIST_FAILED'));
                    return;
                }

                if (result.affectedTeams) {
                    updateTeams(result.affectedTeams);
                }

                updateGames(result.affectedGames);

                resolve(result);
            }).catch(reject).finally(() => {
                ScoreTrekBattle.View.loading('League.Matchday.Details.triggerGameAction', false);
            });
        });
    }

    function startGame(game) {
        ScoreTrekBattle.Tournament.Table.select(game).then(table => {
            triggerGameAction('start', {
                gameId: game.id,
                tableId: table.id
            }).catch(error => {
                if (error.message === 'TABLE_HAS_GAME') {
                    chmDialog.info(__i18n('table/hasGame', {tableName: table.name, tableId: table.id}));
                    return;
                }

                chmDialog.error(error);
            });
        }).catch(error => {
            if (!error) {
                return;
            }

            chmDialog.error(error, {
                tableType: __i18n(`table/type/option/${error.errorData ? (error.errorData.tableType || '') : ''}`)
            });
        });
    }

    function finishGame(gameId, currentGameResult, currentError) {
        ScoreTrekBattle.Tournament.GameEditor.result(gameId, currentGameResult, currentError).then(gameResult => {
            triggerGameAction('finish', {
                gameId: gameId,
                gameResult: gameResult
            }).catch(error => {
                finishGame(gameId, gameResult, error);
            });
        }).catch(error => {
            if (!error) {
                return;
            }

            chmDialog.error(error);
        });
    }

    /**
     * Aendert das Ausspielziel einer einzelnen Partie.
     *
     * Das Ziel kommt eigentlich aus dem Liga-Preset und gilt fuer die ganze
     * Liga. Faellt am Spieltag auf, dass es nicht passt — falsch gepflegtes
     * Preset, kurzfristige Absprache —, haette die Turnierleitung sonst keine
     * Handhabe. Die Aenderung gilt nur fuer diese Partie, das Preset bleibt.
     */
    function editGameTarget(gameId, gameData, currentError) {
        ScoreTrekBattle.Tournament.GameEditor.target(gameId, gameData, currentError).then(gameData => {
            gameData.gameId = gameId;

            triggerGameAction('target', gameData).catch(error => {
                editGameTarget(gameId, gameData, error);
            });
        }).catch(error => {
            if (!error) {
                return;
            }

            chmDialog.error(error);
        });
    }

    /**
     * Der Info-Dialog eines Matches — dasselbe Element wie im Turnier.
     *
     * Was dort die Herkunft der Teams und den Weg des Siegers zeigt, entfaellt
     * hier: die Paarungen eines Spieltags stehen mit der Aufstellung fest, es
     * gibt weder ein Vor- noch ein Folge-Match. An die Stelle tritt der
     * Durchgang, denn das ist die Einordnung, die ein Liga-Match hat.
     */
    function showMatchInfo(game) {
        let round, match;

        round = getRoundByMatchIndex(getMatchIndexOfGame(game));
        match = getPresetMatch(round, getMatchIndexOfGame(game));

        ScoreTrekBattle.Template.render('tournament/match/info', {
            game: game,
            tournament: STATUS.matchday,
            gameStatus: game.status,
            timeStatus: getTimeStatus(game),
            tableName: (game.tableName === '') ? __i18n('table/none') : game.tableName,
            scoreboardName: (game.scoreboardName === '') ? __i18n('table/scoreboard/none') : game.scoreboardName
        }).then(view => {
            // Ohne Turnierbaum gibt es dazu nichts zu sagen.
            view.$view.find('[data-id="team1From"],[data-id="team2From"],[data-id="winnerTo"],[data-id="loserTo"]').remove();
            view.$view.find('[data-id="tieInfo"],[data-id="tieNotice"],[data-id="matchInfo"]').remove();

            // Im Turnier steht hier die Art des Matches (Gruppe, K.o., Finale).
            // Am Spieltag ist das der Durchgang — die einzige Einordnung, die
            // ein Liga-Match hat.
            view.$view.find('[data-id="matchType"]').text(round ? round.name : '');

            view.$view.find('[data-id="timeLimitWrapper"]').css('display', game.timeLimitMinutes ? '' : 'none');

            ScoreTrekBattle.Tournament.Live.applyBreakMode(view.$view, game.gameType, !!(match && match.alternatingBreak));

            view.$view.attr('data-match-index', getMatchIndexOfGame(game));

            applyGameData(view.$view, game, false).then(() => {
                chmDialog({
                    title: __i18n('dialog/match/info/title', {
                        gameName: ScoreTrekBattle.Tournament.Live.getGameName(match ? match.index : game.number)
                    }),
                    content: view.$view
                });
            });
        });
    }

    function getMatchIndexOfGame(game) {
        return parseInt(game.number, 10);
    }

    /**
     * Aendert das Zeitlimit einer einzelnen Partie. Wie beim Ausspielziel: die
     * Vorgabe kommt aus dem Liga-Preset, muss am Spieltag aber korrigierbar
     * sein. Die Aenderung gilt nur fuer diese Partie.
     */
    function editGameTimeLimit(gameId, gameData, currentError) {
        ScoreTrekBattle.Tournament.GameEditor.timeLimit(gameId, gameData, currentError).then(gameData => {
            gameData.gameId = gameId;

            triggerGameAction('timeLimit', gameData).catch(error => {
                editGameTimeLimit(gameId, gameData, error);
            });
        }).catch(error => {
            if (!error) {
                return;
            }

            chmDialog.error(error);
        });
    }

    function getMatchName($game) {
        return $game.find('[data-id="matchName"]').first().text();
    }

    // Der Verlauf haengt an Disziplin und Status, nicht an der Art des Events —
    // ein Liga-Match mit Verlauf bekommt denselben Auszug wie ein Turnier-Match.
    function getGamePdf(gameId) {
        ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf', true);

        Api.request('/battle/game/history', {
            gameId: gameId
        }).then(result => {
            if (!result || !result.history) {
                chmDialog.error('error/GAME_HAS_NO_HISTORY');
                return;
            }

            ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf.generateHistory', true);
            Pdf.generateHistory(result.history).then(pdf => {

                ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf.storeGamePdf', true);
                FileManager.storeGamePdf(pdf.fileBase64, pdf.fileName).then(url => {
                    FileManager.openWindow(
                        url,
                        __i18n('pdf/generated/title'),
                        __i18n('pdf/generated/text'),
                        __i18n('button/download'),
                        '<i class="fas fa-download"></i>'
                    );
                }).catch(error => {
                    console.warn(error);
                    Pdf.showError();
                }).finally(() => {
                    ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf.storeGamePdf', false);
                });

            }).catch(error => {
                console.error(error);
                Pdf.showError();
            }).finally(() => {
                ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf.generateHistory', false);
            });

        }).catch(error => {
            chmDialog.error(error);
        }).finally(() => {
            ScoreTrekBattle.View.loading('League.Matchday.Details.getGamePdf', false);
        });
    }

    function triggerMatchdayAction(action) {
        ScoreTrekBattle.View.loading('League.Matchday.Details.triggerMatchdayAction', true);

        Api.request(`/battle/league/matchday/${action}`, {
            id: STATUS.matchday.id
        }).then(() => {
            refreshMatchday().catch(ignore => {});
        }).catch(error => {
            if (error && error.message) {
                chmDialog.error(error);
            }
        }).finally(() => {
            ScoreTrekBattle.View.loading('League.Matchday.Details.triggerMatchdayAction', false);
        });
    }

    _this = {
        getCurrentName: function(matchdayId) {
            let hasMatchday;

            hasMatchday = STATUS.matchday && (!matchdayId || (STATUS.matchday.id === matchdayId));

            return hasMatchday ? STATUS.matchday.title : __i18n('share/name/matchday');
        },

        getShareHash: function(matchdayId) {
            return `/league/matchday/${matchdayId}`;
        }
    };

    ScoreTrekBattle.View.register('/league/matchday', onViewRender, onViewLoad, onViewUpdate, onViewUnload);

    ScoreTrekBattle.Event.on('window.scrolled', function(event, scrollTop) {
        if (!STATUS.active) {
            return;
        }

        storeScrollTop(scrollTop);
    });

    ScoreTrekBattle.Event.on('action.match.start', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        startGame(game);
    });

    ScoreTrekBattle.Event.on('action.match.finish', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        finishGame(game.id);
    });

    ScoreTrekBattle.Event.on('action.match.cancel', function() {
        let game, $game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        $game = $(this).closest('[data-game-id]');

        chmDialog.confirm(__i18n('dialog/confirm/cancelMatch/text', {matchName: getMatchName($game)}, true), {
            title: __i18n('dialog/confirm/cancelMatch/title'),
            buttonYes: __i18n('button/cancelMatch'),
            buttonNo: __i18n('button/no')
        }).then(() => {
            triggerGameAction('cancel', {gameId: game.id}).catch(error => {
                chmDialog.error(error);
            });
        }).catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.match.info', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        showMatchInfo(game);

        return false;
    });

    ScoreTrekBattle.Event.on('action.matchday.player.remove', function() {
        let $item, lineup;

        if (!STATUS.active || !STATUS.matchday) {
            return;
        }

        $item = $(this).closest('[data-matchday-player-id]');
        lineup = getLineup($item.closest('[data-side]').attr('data-side') || '');

        if (!canEditRoster(lineup)) {
            return;
        }

        removeRosterPlayer(
            $item.attr('data-matchday-player-id') || '',
            $item.find('[data-id="playerName"]').text()
        );

        return false;
    });

    ScoreTrekBattle.Event.on('action.matchday.player.info', function() {
        showPlayerInfo($(this).closest('[data-matchday-player-id]').attr('data-matchday-player-id'));

        return false;
    });

    ScoreTrekBattle.Event.on('action.match.editTimeLimit', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        editGameTimeLimit(game.id);
    });

    ScoreTrekBattle.Event.on('action.match.editTarget', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        editGameTarget(game.id);
    });

    ScoreTrekBattle.Event.on('action.match.pdf', function() {
        let game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        getGamePdf(game.id);
    });

    ScoreTrekBattle.Event.on('action.match.reset', function() {
        let game, $game;

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        $game = $(this).closest('[data-game-id]');

        chmDialog.confirm(__i18n('dialog/confirm/resetMatch/text', {matchName: getMatchName($game)}, true), {
            title: __i18n('dialog/confirm/resetMatch/title'),
            buttonYes: __i18n('button/reset'),
            buttonNo: __i18n('button/cancel')
        }).then(() => {
            triggerGameAction('reset', {gameId: game.id}).catch(error => {
                chmDialog.error(error);
            });
        }).catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.match.randomize', function(event) {
        let game, $game;

        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        game = getOwnGame(this);

        if (game === null) {
            return;
        }

        $game = $(this).closest('[data-game-id]');

        ScoreTrekBattle.Tournament.Live.confirmRandomize('randomizeMatch', event, getMatchName($game)).then(() => {
            triggerGameAction('finish', {
                gameId: game.id,
                gameResult: ScoreTrekBattle.Tournament.Live.getRandomGameResult(game.gameTarget)
            }).catch(error => {
                chmDialog.error(error);
            });
        }).catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.matchday.finalize', function() {
        if (!STATUS.matchday) {
            return;
        }

        chmDialog.confirm(__i18n('dialog/confirm/finalizeMatchday/text', {matchdayName: STATUS.matchday.title}, true), {
            title: __i18n('dialog/confirm/finalizeMatchday/title'),
            buttonYes: __i18n('matchday/button/finalize'),
            buttonNo: __i18n('button/cancel')
        }).then(() => {
            triggerMatchdayAction('finalize');
        }).catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.matchday.unseal', function() {
        if (!STATUS.matchday) {
            return;
        }

        triggerMatchdayAction('unseal');
    });

    // Heim und Gast sind eine Darstellung — die Aufstellung wird ueber die
    // stabile Zuordnung own/opponent adressiert.
    ScoreTrekBattle.Event.on('action.matchday.lineup.edit', function(event, roundIndex, side) {
        let lineup;

        if (!STATUS.matchday) {
            return;
        }

        lineup = getLineup(side);

        ScoreTrekBattle.League.Matchday.LineupEditor.open(
            STATUS.matchday.id,
            parseInt(roundIndex, 10),
            lineup ? lineup.side : null
        ).then(() => {
            refreshMatchday().catch(ignore => {});
        });
    });

    // Eine bereits enthuellte Aufstellung noch einmal oeffnen. Bewusst mit
    // Rueckfrage: die Aenderung wird gezaehlt und am Spieltag ausgewiesen.
    ScoreTrekBattle.Event.on('action.matchday.lineup.reopen', function(event, roundIndex) {
        let round;

        if (!STATUS.matchday) {
            return;
        }

        round = STATUS.rounds.find(entry => {
            return (entry.index === parseInt(roundIndex, 10));
        });

        chmDialog.confirm(__i18n('dialog/confirm/reopenLineup/text', {roundName: round ? round.name : ''}, true), {
            title: __i18n('dialog/confirm/reopenLineup/title'),
            buttonYes: __i18n('matchday/editor/button/reopen'),
            buttonNo: __i18n('button/cancel')
        }).then(() => {
            ScoreTrekBattle.League.Matchday.LineupEditor.reopen(
                STATUS.matchday.id,
                parseInt(roundIndex, 10)
            ).then(() => {
                refreshMatchday().catch(ignore => {});
            });
        }).catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.matchday.refresh', function() {
        refreshMatchday().catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('action.matchday.menu.close', function() {
        if (STATUS.$view) {
            ScoreTrekBattle.$body.removeClass('sideMenu-open');
        }
    });

    ScoreTrekBattle.Event.on('action.matches.running', function() {
        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        showMatchesDialog('running');
    });

    ScoreTrekBattle.Event.on('action.matches.done', function() {
        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        showMatchesDialog('done');
    });

    ScoreTrekBattle.Event.on('action.matches.all', function() {
        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        showMatchesDialog();
    });

    ScoreTrekBattle.Event.on('action.match.goto', function() {
        let gameId;

        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        ScoreTrekBattle.$body.removeClass('sideMenu-open');

        gameId = $(this).attr('data-game-id');

        if (STATUS.matchesDialog) {
            STATUS.gotoGameId = gameId;
            STATUS.matchesDialog.dialog.close();
            return;
        }

        gotoGame(gameId);
    });

    ScoreTrekBattle.Event.on('action.match.next', function() {
        let game, stats, $finalization;

        if (!STATUS.matchday || !STATUS.$view) {
            return;
        }

        game = findGameByStatus('ready');

        if (game) {
            gotoGame(game.id);
            return;
        }

        stats = getMatchStats();
        $finalization = STATUS.$view.find('[data-id="finalization"]');

        if ((stats.total > 0) && (stats.done === stats.total) && !isFinalized() && $finalization.is(':visible')) {
            AnimationHandler.scrollIntoView($finalization).then(() => {
                AnimationHandler.pulse($finalization);
            });
            return;
        }

        chmDialog.info(__i18n((stats.done === stats.total) ? 'match/next/done' : 'match/next/none'));
    });

    ScoreTrekBattle.Event.on('action.tables.usage', function() {
        if (!STATUS.matchday) {
            return;
        }

        ScoreTrekBattle.Tournament.Table.usage(STATUS.matchday, 'matchday').then(table => {
            if (!table || table.isAvailable) {
                chmDialog.info(__i18n('table/usage/dialog/unused'));
                return;
            }

            if (table.currentEventId !== STATUS.matchday.id) {
                chmDialog.info(__i18n('table/usage/dialog/foreignEvent', {tableName: table.name}));
                return;
            }

            gotoGame(table.currentGameId);
        }).catch(error => {
            if (!error) {
                return;
            }

            chmDialog.error(error, {
                tableType: __i18n(`table/type/option/${error.errorData ? (error.errorData.tableType || '') : ''}`)
            });
        });
    });

    // Sichtbarkeit, Duplizieren und Loeschen liegen in League.Matchday.Actions,
    // weil Liste und Detailansicht dasselbe Menue anbieten.
    ScoreTrekBattle.Event.on('adminMode.toggle', function() {
        if (!STATUS.active || !STATUS.matchday) {
            return;
        }

        refreshMatchday().catch(ignore => {});
    });

    ScoreTrekBattle.Event.on('matchday.updated', function(matchdayId) {
        if (!STATUS.matchday || (matchdayId !== STATUS.matchday.id)) {
            return;
        }

        refreshMatchday().catch(ignore => {});
    });

    return _this;
})();
